/* eslint-disable react/no-unescaped-entities */
import MarkdownWrapper from '@components/ui/markdown-wrapper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Grid } from '@mui/material';
import MuiAccordion, { AccordionProps as MuiAccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, {
  AccordionSummaryProps as MuiAccordionSummaryProps
} from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { FAQ } from '@wallet/config/types';
import * as React from 'react';
import { customConfigFaq } from './configs';

// Define types for Accordion props
interface AccordionProps extends MuiAccordionProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, expanded: boolean) => void;
}

// Styled Accordion component
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: '1px solid #A0A08E',
  borderRadius: '8px',
  padding: '1em',
  margin: '1em'
}));

// Styled AccordionSummary component
const AccordionSummary = styled((props: MuiAccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowDropDownIcon
        sx={{
          fontSize: '1.8rem'
        }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1)
  }
}));

// Styled AccordionDetails component
const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  // Add any specific styling needed here
}));

// Main Faq component
export default function Faq() {
  const [expanded, setExpanded] = React.useState<string | false>('');
  const faqConfigs: readonly FAQ[] = customConfigFaq();

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Grid sx={{ padding: '2px' }}>
      <h2>Frequently Asked Questions</h2>

      {faqConfigs.length > 0 &&
        faqConfigs.map((f: FAQ, i: number) => (
          <Accordion
            key={`panel${i}`}
            expanded={expanded === `panel${i}`}
            onChange={handleChange(`panel${i}`)}>
            <AccordionSummary aria-controls={`panel${i}d-content`} id={`panel${i}d-header`}>
              <Typography sx={{ fontWeight: '600' }}>{f.question}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#000000', ml: '1.85rem' }}>
              <Typography sx={{ color: '#000000' }}>
                <MarkdownWrapper>{f.answer}</MarkdownWrapper>
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}

      <Accordion expanded={expanded === 'panel-46456'} onChange={handleChange('panel-46456')}>
        <AccordionSummary aria-controls="panel-46456d-content" id="panel-46456d-header">
          <Typography sx={{ fontWeight: '600' }}>
            Is it possible to submit additional documents for verification after making a payment?{' '}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Absolutely, you have the option to submit more documents after you've made a payment.
            Here's how you can do it:
          </Typography>
          <ol>
            <Typography component="li" variant="body1">
              Log into your account.{' '}
            </Typography>
            <Typography component="li" variant="body1">
              Navigate to the Verification Form and then to the Required Verification section.{' '}
            </Typography>
            <Typography component="li" variant="body1">
              Begin uploading your documents.
            </Typography>
            <Typography component="li" variant="body1">
              Once you've finished uploading, you'll need to make a payment for the extra
              verifications. To do this, click on the 'Click to Pay' button located at the bottom of
              the page.
            </Typography>
            <Typography component="li" variant="body1">
              Choose your preferred payment method and complete the transaction.
            </Typography>
            <Typography component="li" variant="body1">
              After the payment is successful, the newly added documents will be sent for
              verification.
            </Typography>{' '}
          </ol>
          <Typography>
            Please note: If you add documents after making a payment, the verification process will
            take longer. This is because additional time is needed to verify the new documents.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
